import { onUnmounted, ref } from "vue";

export type SaveStatus = "idle" | "saving" | "saved" | "error";

export interface SaveState {
	status: SaveStatus;
	lastSavedAt: number | null;
}

/** Save state before anything has been persisted — shared so the shape isn't re-typed at each default. */
export const IDLE_SAVE_STATE: SaveState = { status: "idle", lastSavedAt: null };

export interface UseAutosaveOptions {
	/** Idle delay before an autosave fires. Each edit restarts it (debounce). */
	delayMs?: number;
	/** The actual persist action — typically `() => save({ isAutosave: true })`. */
	performAutosave: () => void | Promise<void>;
}

/** Default idle delay before an autosave fires — 3s per LSP-16953 (was 10s under LSP-16420). */
export const AUTOSAVE_DELAY_MS = 3_000;

/**
 * Debounced autosave timer + live save-status state for LSP-16420.
 *
 * Owns only the countdown and the status/lastSavedAt refs; the caller owns the
 * real persistence (and its single-flight guard) and reports the outcome back
 * via markSaving / markSaved / markError. lastSavedAt is set on every successful
 * save (manual or auto) so the "Last saved …" indicator can render.
 */
export function useAutosave({ delayMs = AUTOSAVE_DELAY_MS, performAutosave }: UseAutosaveOptions) {
	const saveStatus = ref<SaveStatus>("idle");
	const lastSavedAt = ref<number | null>(null);

	let timerId: ReturnType<typeof setTimeout> | null = null;

	function cancel(): void {
		if (timerId) {
			clearTimeout(timerId);
			timerId = null;
		}
	}

	/** Restart the idle countdown — rapid edits collapse into a single save. */
	function schedule(): void {
		cancel();
		timerId = setTimeout(() => {
			timerId = null;
			void performAutosave();
		}, delayMs);
	}

	function markSaving(): void {
		saveStatus.value = "saving";
	}

	function markSaved(): void {
		saveStatus.value = "saved";
		lastSavedAt.value = Date.now();
	}

	function markError(): void {
		saveStatus.value = "error";
	}

	// Return to the pristine, never-saved state — e.g. when the bound entity is swapped, so a
	// previous entity's "Last saved …" doesn't bleed onto the newly loaded one. Also cancels any timer.
	function reset(): void {
		cancel();
		saveStatus.value = "idle";
		lastSavedAt.value = null;
	}

	onUnmounted(cancel);

	return { saveStatus, lastSavedAt, schedule, cancel, reset, markSaving, markSaved, markError };
}
